import { useEffect, useState, Component } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { copy, CONFIG } from "./i18n";
import { LIMPIEZA, LIMPIEZA_EN } from "./limpiezaCopy";
import { GUIA, GUIA_EN } from "./guiaCopy";
import { initAnalytics, trackPageView, trackEvent } from "./analytics";
import Cursor from "./components/Cursor";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import Services from "./components/Services";
import Elegancia from "./components/Elegancia";
import Work from "./components/Work";
import CtaBand from "./components/CtaBand";
import About from "./components/About";
import Process from "./components/Process";
import Contact from "./components/Contact";
import PrivacyPage from "./components/PrivacyPage";
import LimpiezaPage from "./components/LimpiezaPage";
import GuiaPage from "./components/GuiaPage";
import NotFoundPage from "./components/NotFoundPage";
import Footer from "./components/Footer";
import WhatsAppFab from "./components/WhatsAppFab";

class ErrorBoundary extends Component {
  state = { err: false };
  static getDerivedStateFromError() { return { err: true }; }
  render() {
    return this.state.err ? (
      <div style={{ padding: 80, fontFamily: "Georgia, serif" }}>
        <h1>Algo salió mal.</h1>
        <p>Recarga la página para continuar.</p>
      </div>
    ) : this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Una visita de página por ruta (GA4 + Meta Pixel); medición manual única, sin histórico automático.
const RouteTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
};

const KNOWN_ROUTES = ["/", "/privacidad", "/limpieza", "/guia-como-crear-una-pagina-web"];

export function Site() {
  // Primer render SIEMPRE en español (idéntico al prerender); la preferencia
  // guardada se aplica después de hidratar para evitar el error #418.
  const [lang, setLang] = useState("es");
  const t = copy[lang];
  const { pathname } = useLocation();
  const clean = pathname.replace(/\/+$/, "") || "/";
  const isPrivacy = clean === "/privacidad";
  const isLimpieza = clean === "/limpieza";
  const isGuia = clean === "/guia-como-crear-una-pagina-web";
  const is404 = !KNOWN_ROUTES.includes(clean);

  useEffect(() => {
    if (localStorage.getItem("cs-lang") === "en") setLang("en");
  }, []);

  useEffect(() => { initAnalytics(); }, []);

  useEffect(() => {
    document.documentElement.lang = is404 ? "es" : lang;
    document.title = is404
      ? t.notfound.docTitle
      : isPrivacy
        ? t.privacy.docTitle
        : isLimpieza
          ? (lang === "en" ? LIMPIEZA_EN.docTitle : LIMPIEZA.docTitle)
          : isGuia
            ? (lang === "en" ? GUIA_EN.docTitle : GUIA.docTitle)
            : t.title;
    localStorage.setItem("cs-lang", lang);
  }, [lang, t, isPrivacy, isLimpieza, isGuia, is404]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const el = document.getElementById(a.getAttribute("href").slice(1));
      if (el) { e.preventDefault(); lenis.scrollTo(el, { offset: window.innerWidth < 760 ? -104 : -64 }); }
    };
    document.addEventListener("click", onClick);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); document.removeEventListener("click", onClick); };
  }, []);

  // Clics medibles: enlaces de WhatsApp (GA4 whatsapp_click + Meta Contact) y de pricecleanpro.com
  useEffect(() => {
    const onClick = (e) => {
      const wa = e.target.closest && e.target.closest('a[href*="wa.me"]');
      if (wa) {
        trackEvent("whatsapp_click", { ubicacion: wa.classList.contains("wa-fab") ? "flotante" : "enlace" }, "Contact");
        return;
      }
      const pc = e.target.closest && e.target.closest('a[href*="pricecleanpro.com"]');
      if (pc) trackEvent("pricecleanpro_click", {});
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <>
      <Cursor />
      <Header t={t} lang={lang} setLang={setLang} />
      {is404 ? (
        <NotFoundPage t={t} />
      ) : isPrivacy ? (
        <PrivacyPage t={t} />
      ) : isLimpieza ? (
        <LimpiezaPage lang={lang} />
      ) : isGuia ? (
        <GuiaPage lang={lang} />
      ) : (
        <main>
          <Hero t={t} />
          <Marquee items={t.strip} />
          <Services t={t} />
          <Elegancia t={t} />
          <Work t={t} />
          <CtaBand t={t} />
          <About t={t} />
          <Process t={t} />
          <Contact t={t} />
        </main>
      )}
      <Footer t={t} />
      {CONFIG.whatsapp && CONFIG.email && CONFIG.photo ? null : (
        <div className="review-bar mono" data-testid="review-bar">{t.review}</div>
      )}
      <WhatsAppFab lang={lang} customMsg={isLimpieza ? (lang === "en" ? LIMPIEZA_EN.waMsg : LIMPIEZA.waMsg) : undefined} />
    </>
  );
}

// Shell: la estructura EXACTA que hidrata el navegador (mismo árbol que prerender-entry).
// Si cambia aquí, debe cambiar igual en scripts/prerender-entry.jsx para no romper useId.
export const Shell = () => (
  <ErrorBoundary>
    <ScrollToTop />
    <RouteTracker />
    <Site />
  </ErrorBoundary>
);

function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
